/**
 * UpgradePreviewModal Component
 * Preview modal shown BEFORE redirecting to Stripe Checkout
 *
 * Per Inara's feedback: Show user exactly what they're getting
 * and what it will cost before entering payment details
 */

import { Modal, Typography, Space, Button } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import type { PlanTier } from '../../types/billing.types'
import { PLAN_TIERS } from '../../types/billing.types'

const { Title, Text, Paragraph } = Typography

interface Props {
  visible: boolean
  currentTier: PlanTier
  newTier: PlanTier
  billingPeriod: 'monthly' | 'annual'
  onConfirm: () => void
  onCancel: () => void
  loading?: boolean
}

export function UpgradePreviewModal({
  visible,
  currentTier,
  newTier,
  billingPeriod,
  onConfirm,
  onCancel,
  loading = false
}: Props) {
  // Defensive: Fall back to 'free' tier if tiers are invalid
  const currentPlan = PLAN_TIERS[currentTier] ?? PLAN_TIERS.free
  const newPlan = PLAN_TIERS[newTier] ?? PLAN_TIERS.free

  const price = billingPeriod === 'annual' && newPlan.priceAnnual
    ? newPlan.priceAnnual
    : newPlan.priceMonthly

  const billingText = billingPeriod === 'annual'
    ? `$${price}/year`
    : `$${price}/month`

  // Calculate savings for annual
  const savings = billingPeriod === 'annual' && newPlan.priceAnnual
    ? (newPlan.priceMonthly * 12 - newPlan.priceAnnual)
    : 0

  return (
    <Modal
      title={`Upgrade to ${newPlan.displayName}?`}
      open={visible}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="confirm" type="primary" onClick={onConfirm} loading={loading}>
          Continue to Payment
        </Button>
      ]}
      width={500}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Plan comparison */}
        <div>
          <Text type="secondary">Current Plan: </Text>
          <Text strong>{currentPlan.displayName} (${currentPlan.priceMonthly}/mo)</Text>
        </div>
        <div>
          <Text type="secondary">New Plan: </Text>
          <Text strong>{newPlan.displayName} ({billingText})</Text>
        </div>

        {/* Savings callout for annual */}
        {savings > 0 && (
          <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '4px' }}>
            <Text strong style={{ color: '#1890ff' }}>
              Save ${savings} per year with annual billing!
            </Text>
          </div>
        )}

        {/* What you'll get */}
        <div>
          <Title level={5} style={{ marginBottom: '12px' }}>What You'll Get:</Title>
          <Space direction="vertical">
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckOutlined style={{ color: '#52c41a' }} />
              <Text>
                {typeof newPlan.skuLimit === 'number' ? newPlan.skuLimit.toLocaleString() : newPlan.skuLimit} SKUs
                {currentPlan.skuLimit !== newPlan.skuLimit && (
                  <Text type="secondary"> (up from {currentPlan.skuLimit.toLocaleString()})</Text>
                )}
              </Text>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <CheckOutlined style={{ color: '#52c41a' }} />
              <Text>
                {newPlan.smartimportLimit === 'Unlimited' ? 'Unlimited' : newPlan.smartimportLimit} SmartImport runs
                {currentPlan.smartimportLimit !== newPlan.smartimportLimit && (
                  <Text type="secondary">
                    {' '}(up from {currentPlan.smartimportLimit === 'Unlimited' ? 'Unlimited' : currentPlan.smartimportLimit})
                  </Text>
                )}
              </Text>
            </div>
            {newPlan.features.map((feature, index) => (
              <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CheckOutlined style={{ color: '#52c41a' }} />
                <Text>{feature}</Text>
              </div>
            ))}
          </Space>
        </div>

        {/* Billing preview */}
        <div style={{ background: '#fafafa', padding: '12px', borderRadius: '4px' }}>
          <Title level={5} style={{ marginBottom: '8px' }}>Billing Preview:</Title>
          <Paragraph style={{ margin: 0 }}>
            <Text type="secondary">Today's charge: </Text>
            <Text strong>Prorated amount (calculated by Stripe)</Text>
          </Paragraph>
          <Paragraph style={{ margin: 0 }}>
            <Text type="secondary">Next billing: </Text>
            <Text strong>{billingText}</Text>
          </Paragraph>
        </div>
      </Space>
    </Modal>
  )
}
